// Ported from dev-scrips-pm-react src/scheduling/components/appointments/create-appointment-panel.tsx (PROD-1190 calendar port)
import { useFormContext } from 'react-hook-form';
import { AppointmentFormProvider } from './appointment-form-provider';
import { AppointmentStatusManager } from './appointment-status-manager';
import { CancelAppointmentDialog } from './cancel-appointment-dialog';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { PanelFooter } from './sections/panel-footer';
import { AppointmentStatusSummary } from './sections/appointment-status-summary';
import { LocationSelector } from './sections/location-selector';
import { ProviderSection } from './sections/provider-section';
import { AppointmentProfileSection } from './sections/appointment-profile-section';
import { DatePickerSection } from './sections/date-picker-section';
import { TimeSlotsSection } from './sections/time-slots-section';
import { PatientSection } from './sections/patient-section';
import { CallbackContactSection } from './sections/callback-contact-section';
import { PaymentSection } from './sections/payment-section';
import { ReminderSection } from './sections/reminder-section';
import { ResidentialAddressSection } from './sections/residential-address-section';
import { PreviousAppointmentSection } from './sections/previous-appointment-section';
import { AppointmentNoteSection } from './sections/note-section';
import { FlagsSection } from './sections/flags-section';
import { RecurrenceSection } from './sections/recurrence-section';
import { ProviderSelectionView } from './sections/provider-selection-view';
import { TimeDateSelectionView } from './sections/time-date-selection-view';
import { FlagsPickerView } from './sections/flags-picker-view';
import { FormsPickerView } from './sections/forms-picker-view';
import { ReminderPickerView } from './sections/reminder-picker-view';
import { ReminderEditorView } from './sections/reminder-editor-view';
import { RecurrencePickerView } from './sections/recurrence-picker-view';
import { CreateContactView } from './sections/create-contact-view';
import { EditInsuranceView } from './sections/edit-insurance-view';
import { CreatePatientView } from './sections/create-patient-view';
import { toast } from 'sonner';
import { useBookAppointment } from '@/services/scheduling/use-book-appointment';
import { useUpdateAppointment } from '@/services/scheduling/use-update-appointment';
import { transformFormToRequest } from './transform-form-to-request';
import { buildEditAppointmentBody } from '@/scheduling/lib/form-edit-overlay';
import { cn } from '@/lib/utils';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/shared/api/api-client';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { AppointmentResponse } from '@/types/scheduling-api';
import type { HealthInsurance } from '@/services/scheduling/use-prebook-selected-patient';
import { useFormContext as useFC } from 'react-hook-form';
import { Button, Icon, Modal, type IconName } from '@scripsteam/scrips-signal-ds';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * CreateAppointmentPanel — Block (inline right panel, the Practice app's: Antonin, 15 Oct)
 * Section order is Practice's, not Flutter's slot order:
 *   Provider → Location → Profile → Date & Time → Patient
 *   → Previous Appointment (FOLLOWUP) → Residential Address (Home)
 *   → Callback Contact → Reminder → Payment → Note → Flags → Recurrence → Actions
 *
 * Profile drives slot duration / reasonCode / room / billing — must come
 * before Date & Time so slot lookup can filter by appointmentProfileId.
 * Rendered inline on the RIGHT of the calendar (the page's `overlays` slot) — NOT as a Sheet/drawer.
 * "Open in full view" moves the same form (one instance, nothing remounted) into a centred dialog.
 */

interface CreateAppointmentPanelProps {
  open: boolean;
  onClose: () => void;
  /** Leave out the "Open in full view" button (the routed appointment page: the panel already has the room). */
  hideFullView?: boolean;
  mode?: 'create' | 'edit' | 'reschedule' | 'cancel';
  organizationId: string;
  practiceId: string;
  roomId?: string;
  userId: string;
  onSearchPatients: (query: string) => Promise<Array<{
    id: string;
    firstName: string;
    lastName: string;
    mrn?: string;
    dob?: string;
    phone?: string;
  }>>;
  initialValues?: Partial<AppointmentFormValues>;
  /**
   * The loaded appointment, REQUIRED in `mode="edit"`. Save overlays the
   * edited form fields onto this payload and PUTs it (Flutter
   * `UpdateAppointmentEvent(selectedAppointment)` — the update body is the
   * whole appointment echoed back, NOT a Book-shaped request). Ignored in
   * create mode.
   */
  editAppointment?: AppointmentResponse | null;
  /**
   * Whether to render the Payment/sponsor section. Mirrors Flutter
   * `create_appointment_widget.dart:1613` — the PAYMENT block is gated on the
   * `Show_Billing_Summary` permission (DATA, not app-type), same as the View
   * popup. Default true so non-permission-aware callers keep the section.
   * PROD-1190.
   */
  showBillingSummary?: boolean;
  /** Edit mode: "Proceed to complete patient" from the rail's Mark As Arrived guard (the page's own handler). */
  onCompletePatient?: () => void;
  /**
   * Edit mode: open the rail straight in its Reschedule or Cancel state (the design's X1 / X2 rail modes; the page reads
   * it from `?tab=reschedule|cancel`). Close in that state goes back to the edit rail.
   */
  startIn?: 'reschedule' | 'cancel';
}

/**
 * SendFormRow — selected-form card matching Flutter
 * `questionnaire_widget.dart:37–101`: send icon + form name (bold) + status
 * + edit pencil (opens picker) + delete trash (clears the selection).
 * Renders only when a form has been picked.
 */
function SendFormRow({ onEdit }: { onEdit?: () => void }) {
  const { watch, setValue } = useFC<AppointmentFormValues>();
  const id = watch('questionnaireId');
  const name = watch('questionnaireName');
  const status = watch('questionnaireStatus');
  if (!id) return null;

  const clear = () => {
    setValue('questionnaireId', null);
    setValue('questionnaireName', null);
    setValue('questionnaireStatus', null);
  };

  return (
    <div className="booking-section" data-slot="booking-section">
      <p className="booking-section-label">
        Send form
      </p>
      <div className="booking-card flex items-center">
        <Icon name="send" size={20} aria-hidden />
        <div className="flex-1 min-w-0">
          <p className="booking-card-title truncate">
            {name ?? '—'}
          </p>
          {status ? (
            <p className="booking-card-meta">{status}</p>
          ) : null}
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="booking-icon-btn"
            aria-label="Edit form"
          >
            <Icon name="edit" size={16} aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          onClick={clear}
          className="booking-icon-btn"
          aria-label="Clear form"
        >
          <Icon name="close" size={16} aria-hidden />
        </button>
      </div>
    </div>
  );
}

/**
 * ActionsRow — Flutter parity for the bottom 5-icon button strip
 * (`create_appointment_widget.dart:1893–2097`). Each icon shows ONLY
 * when its section is empty; tapping reveals the section above by
 * seeding its form fields. Recurrence is gated on the same prereqs
 * Flutter checks (provider + profile + billingProfileId + slot + patient).
 */
function ActionsRow({
  onOpenFlags,
  onOpenSendForm,
  onOpenReminder,
  onOpenRecurrence,
  reminderAddDisabled = false,
  hideRecurrence = false,
}: {
  onOpenFlags?: () => void;
  onOpenSendForm?: () => void;
  onOpenReminder?: () => void;
  onOpenRecurrence?: () => void;
  /**
   * Disable ONLY the Reminder add-button (Flutter `reminderButtonWidget`
   * `:1947`): unlike Note / Flag / Send-Form (Booked/Proposed/Pending), the
   * reminder add-button is gated to Booked/Proposed — a Pending appointment
   * may NOT add a reminder. The other buttons stay enabled.
   */
  reminderAddDisabled?: boolean;
  /** Reschedule: no Recurrence button (the design's add row and PM's `hideRecurrence`). */
  hideRecurrence?: boolean;
}) {
  const { watch, setValue } = useFC<AppointmentFormValues>();
  const comment = watch('comment');
  const recurring = watch('recurring');
  const reminderProfileId = watch('reminderProfileId');
  const questionnaireId = watch('questionnaireId');
  const patientFlags = watch('patientFlags');
  const flagIds = watch('flagIds');

  const providerId = watch('providerId');
  const profileId = watch('appointmentProfileId');
  const billingProfileId = watch('billingProfileId');
  const slotStart = watch('slotStart');
  const slotEnd = watch('slotEnd');
  const patientId = watch('patientId');
  void slotStart;
  void slotEnd;
  void setValue;

  const recurrenceEnabled =
    !!providerId && !!profileId && !!billingProfileId && !!slotStart && !!slotEnd && !!patientId;

  const showNote = comment !== null;
  const showReminder = !!reminderProfileId;
  const showSendForm = !!questionnaireId;
  const showFlag = (patientFlags?.length ?? 0) > 0 || (flagIds?.length ?? 0) > 0;
  const showRecurrence = !!recurring;

  // All sections set → no icons left to render → hide the row entirely.
  if (showNote && showReminder && showSendForm && showFlag && showRecurrence) {
    return null;
  }

  // The design's add row (SchedBookingForm `.actions` / `.actionBtn`): one wrapping row of 32-tall hairline buttons, the DS
  // icon before the word, in the design's order and words (Note, Flags, Send form, Reminder, Recurrence). Each still shows
  // only while its section is empty and keeps its own enable rule.
  const buttons: Array<{
    key: string;
    icon: IconName;
    label: string;
    visible: boolean;
    enabled?: boolean;
    onClick: () => void;
  }> = [
    {
      key: 'note',
      icon: 'message',
      label: 'Note',
      visible: !showNote,
      onClick: () => setValue('comment', ''),
    },
    {
      key: 'flag',
      icon: 'flag',
      label: 'Flags',
      visible: !showFlag,
      // Opens the full-screen Flags picker (Flutter
      // `flags_selection_widget.dart`). Disabled until a patient is
      // picked — the picker pre-checks based on `patientFlags`, which
      // is seeded by PreBookSelectedPatient on patient pick.
      enabled: !!patientId && !!onOpenFlags,
      onClick: () => onOpenFlags?.(),
    },
    {
      key: 'send-form',
      icon: 'file',
      label: 'Send form',
      visible: !showSendForm,
      // Opens the full-screen Forms picker (Flutter
      // `forms_selection_widget.dart`). The picker scopes by provider,
      // so it's only enabled once a provider is selected.
      enabled: !!providerId && !!onOpenSendForm,
      onClick: () => onOpenSendForm?.(),
    },
    {
      key: 'reminder',
      icon: 'bell',
      label: 'Reminder',
      visible: !showReminder,
      // Auto-populated by PreBookSelectedAppointmentProfile on profile
      // pick; this button opens the manual override picker (Flutter
      // `reminder_profile_selection_widget.dart`). Gated to Booked/Proposed
      // in edit (no Pending) — see `reminderAddDisabled`.
      enabled: !!onOpenReminder && !reminderAddDisabled,
      onClick: () => onOpenReminder?.(),
    },
    {
      key: 'recurrence',
      icon: 'refresh',
      label: 'Recurrence',
      visible: !showRecurrence && !hideRecurrence,
      // Opens the full-screen Recurrence configurator (Flutter
      // `recurrence_appointment_widget.dart`). Same prereqs as Flutter:
      // provider + profile + billing + slot + patient must be set.
      enabled: recurrenceEnabled && !!onOpenRecurrence,
      onClick: () => onOpenRecurrence?.(),
    },
  ];

  return (
    <div className="booking-actions" data-slot="booking-actions">
      {buttons
        .filter((b) => b.visible)
        .map(({ key, icon, label, onClick, enabled = true }) => (
          <button
            key={key}
            type="button"
            onClick={enabled ? onClick : undefined}
            disabled={!enabled}
            className="booking-action-btn"
            title={enabled ? label : `${label} (unavailable)`}
          >
            <Icon name={icon} size={16} aria-hidden />
            {label}
          </button>
        ))}
    </div>
  );
}

/** AppointmentNoteSection wrapper that hides until the Note button reveals it.
 * Mirrors Flutter's `comment != null` gate (slot 10): bottom-row icon
 * sets `comment` to '' to reveal; the section's clear (×) sets it back
 * to null to re-hide. */
function NoteSlot() {
  const { watch } = useFC<AppointmentFormValues>();
  const comment = watch('comment');
  if (comment === null) return null;
  return <AppointmentNoteSection />;
}

/** RecurrenceSection wrapper — only show once a recurrence has been seeded. */
function RecurrenceSlot({ onEdit }: { onEdit?: () => void }) {
  const { watch } = useFC<AppointmentFormValues>();
  const recurring = watch('recurring');
  if (!recurring) return null;
  return <RecurrenceSection onEdit={onEdit} />;
}

/** Appointment statuses under which the status-gated sections stay editable
 *  in edit mode. Verbatim from Flutter `app_constants.dart` status codes
 *  (`appointmentStatusBooked/Proposed/Pending`). */
const EDITABLE_EDIT_STATUSES = new Set(['Booked', 'Proposed', 'Pending']);

/** Statuses under which the Reminder ADD-button stays enabled in edit mode.
 *  Flutter `reminderButtonWidget` (`:1947`) excludes Pending — narrower than
 *  the other bottom-row buttons (Note / Flag / Send-Form), which allow it. */
const REMINDER_ADD_EDIT_STATUSES = new Set(['Booked', 'Proposed']);

/**
 * SectionLock — read-only wrapper for status-gated sections in edit mode.
 * Flutter wraps locked sections in `IgnorePointer`; `pointer-events-none`
 * is the DOM equivalent. `opacity-60` signals read-only; `aria-disabled`
 * carries it to assistive tech. Passthrough when not locked.
 */
function SectionLock({
  locked,
  children,
}: {
  locked: boolean;
  children: React.ReactNode;
}) {
  if (!locked) return <>{children}</>;
  return (
    <div className="pointer-events-none opacity-60" aria-disabled>
      {children}
    </div>
  );
}

/**
 * Walk a (possibly nested) RHF errors object and return every leaf path.
 * Object.entries on `formState.errors` only sees top-level keys, so nested
 * issues like `emergencyContacts.0.relationship` are invisible — surfacing
 * them here gives the diagnostic tooltip something useful to show.
 */
function collectErrorPaths(errors: unknown, prefix = ''): string[] {
  if (!errors || typeof errors !== 'object') return [];
  const out: string[] = [];
  const e = errors as Record<string, unknown>;
  // Leaf: { type, message, ... }
  if (typeof e.message === 'string' || typeof e.type === 'string') {
    out.push(prefix || '(root)');
    return out;
  }
  for (const [key, val] of Object.entries(e)) {
    if (key === 'ref') continue;
    out.push(...collectErrorPaths(val, prefix ? `${prefix}.${key}` : key));
  }
  return out;
}

/** The header title per mode: Practice's words, sentence case. */
const PANEL_TITLES = {
  create: 'New appointment',
  edit: 'Edit appointment',
  reschedule: 'Reschedule',
  cancel: 'Cancel appointment',
} as const;

function CreateAppointmentForm({
  mode = 'create',
  onClose,
  organizationId,
  practiceId,
  roomId = '',
  userId,
  editAppointment = null,
  showBillingSummary = true,
  onCompletePatient,
  startIn,
  modal = false,
  expanded = false,
  onToggleExpanded,
}: Omit<CreateAppointmentPanelProps, 'open' | 'initialValues' | 'onSearchPatients'> & {
  onSearchPatients?: unknown;
  /** The form is drawn inside the full-view dialog: roomier header and footer. */
  modal?: boolean;
  expanded?: boolean;
  onToggleExpanded?: () => void;
}) {
  const form = useFormContext<AppointmentFormValues>();
  const bookAppointment = useBookAppointment();
  const updateAppointment = useUpdateAppointment();
  // The rail's state (the design's SchedBookingPanel modes; PM's `isEditMode` / `isEditOnly` / `isRescheduleMode`):
  // an edit rail turns into Reschedule (footer "Reschedule") or Cancel (status "Cancel Appointment") and back.
  // Reschedule saves through the same edit save (PM: the same PUT); Cancel opens the same cancel dialog (reason,
  // scope, the same cancel mutations).
  const [railMode, setRailMode] = useState<NonNullable<CreateAppointmentPanelProps['mode']>>(
    mode === 'edit' && startIn ? startIn : mode,
  );
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const hasAppt = !!editAppointment;
  const isEditMode = railMode !== 'create' && hasAppt;
  const isEditOnly = railMode === 'edit' && hasAppt;
  const isRescheduleMode = railMode === 'reschedule' && hasAppt;
  const isCancelMode = railMode === 'cancel' && hasAppt;
  // Close (and the x) in a Reschedule / Cancel state entered from the edit rail goes back to the edit rail.
  const closeRail = mode === 'edit' && railMode !== 'edit' ? () => setRailMode('edit') : onClose;
  const isSubmitting = isEditMode
    ? updateAppointment.isPending
    : bookAppointment.isPending;
  // Status gate (Flutter `showEdit`): the status-gated sections (profile /
  // date-time / patient / reminder / note / flags / forms / recurrence) are
  // editable only when the appointment is Booked / Proposed / Pending. The
  // Provider is ALWAYS locked in edit (own `locked` prop), and Payment is
  // ALWAYS editable (no status gate) — see CONVERSION_NOTES.md 3-tier model.
  const sectionsLocked =
    isCancelMode ||
    (isEditMode && !EDITABLE_EDIT_STATUSES.has(editAppointment?.status?.code ?? ''));
  // Payment lock (Flutter `payment_widget.dart:359`): the sponsor switcher is
  // wrapped in `IgnorePointer(ignoring: (invoiceStatus != Draft && id != null)
  // || status == Cancelled)`. For an existing appointment (always true in edit)
  // that reduces to: editable ONLY while the invoice is still a draft and the
  // appointment isn't cancelled. Identical to the gate shipped in the View
  // popup (PROD-1010). Create mode (id == null) is never locked.
  const invoiceStatus = (editAppointment?.invoice?.invoiceStatus ?? '').toLowerCase();
  const isCancelledStatus =
    (editAppointment?.status?.code ?? '').toLowerCase() === 'cancelled';
  const paymentLocked =
    isCancelMode || (isEditMode && !(invoiceStatus === 'draft' && !isCancelledStatus));
  // Reminder add-button: enabled in edit only for Booked/Proposed (no Pending).
  const reminderAddLocked =
    isEditMode && !REMINDER_ADD_EDIT_STATUSES.has(editAppointment?.status?.code ?? '');
  const qc = useQueryClient();
  const providerId = form.watch('providerId');
  const appointmentProfileId = form.watch('appointmentProfileId');
  const patientId = form.watch('patientId');
  const [view, setView] = useState<
    | 'create'
    | 'provider-selection'
    | 'time-date-selection'
    | 'flags-selection'
    | 'forms-selection'
    | 'reminder-selection'
    | 'reminder-editor'
    | 'recurrence-config'
    | 'create-contact'
    | 'edit-insurance'
    | 'create-patient'
  >('create');

  const handleSave = () => {
    const values = form.getValues();

    // Sponsor save guard — Flutter `onSave` (create_appointment_widget.dart
    // :2487-2501) blocks the save (toast, no mutation) when a non-Self-pay
    // sponsor is invalid. The "no sponsor selected" case is already enforced
    // by the Zod schema (`appointment-form.ts` → Save button disabled), so the
    // only gap is an EXPIRED insurance being the selected sponsor — which the
    // schema can't see. Verbatim Flutter string. Applies to create + edit.
    if (values.sponsorType === 'Insurance') {
      const list = (values.healthInsurances ?? []) as HealthInsurance[];
      const selected = list.find(
        (ins) =>
          !!ins.insuranceProvider?.id &&
          ins.insuranceProvider.id === values.sponsorId &&
          (ins.policyNumber ?? '') === (values.policyNo ?? ''),
      );
      const exp = selected?.expirationDate
        ? Date.parse(selected.expirationDate)
        : Number.NaN;
      if (Number.isFinite(exp) && exp < Date.now()) {
        toast.error('Selected Insurance is expired');
        return;
      }
    }

    // EDIT mode — Flutter `UpdateAppointmentEvent(selectedAppointment)`.
    // Overlay the edited fields onto the loaded payload and PUT (NOT the
    // Book shape). See `form-edit-overlay.ts` + CONVERSION_NOTES.md.
    if (isEditMode && editAppointment) {
      const body = buildEditAppointmentBody(values, editAppointment, userId);
      updateAppointment.mutate(
        { appointmentId: editAppointment.id, body },
        {
          onSuccess: () => {
            toast.success('Appointment updated.');
            onClose();
          },
          // Failures surface via the global MutationCache.onError toast.
        },
      );
      return;
    }

    // The transform now derives speciality + invoice from the form
    // itself; the previous hardcoded `specialty: "general"` was the root
    // cause of the cascading gRPC failure.
    const request = transformFormToRequest(values, {
      organizationId,
      practiceId,
      roomId,
      createdBy: userId,
    });

    // Success: just close — global QueryClient.MutationCache.onError
    // handles failure via showServerError (per main's shared infra
    // rules), so no local logging is needed and no PHI ever lands in
    // the console.
    bookAppointment.mutate(request, {
      onSuccess: () => {
        // Success toast — matches the convention the rest of the
        // scheduling mutations use (BlockSlot, UnblockSlot, FU
        // Cancel/Send Reminder). Without it, the panel closes
        // silently and the user has no positive confirmation that
        // the appointment actually saved.
        toast.success('Appointment booked.');
        // FU Requests refetch — when this Book carried `followUpId`
        // (PROD-920 Create Follow Up flow), the backend marks the
        // originating FU request as completed. Invalidating the
        // `['follow-up-requests']` prefix forces the FU tab's list
        // query to refetch, so the row's status flips from
        // Requested → Completed (or drops out of the active view
        // entirely). Safe no-op for non-FU saves — the FU list
        // either isn't mounted, or refetching it is cheap.
        void qc.invalidateQueries({ queryKey: ['follow-up-requests'] });
        onClose();
      },
      onError: (err) => {
        // SlotPassed recovery — Flutter `calendar_screen.dart:2355-2400`.
        // Server returns 400 with `{ errors: { SlotPassed: [...] } }`
        // when the slot the user picked has already started by the
        // time Book fires (common at the boundary of a 5-min slot,
        // or when the user opened the panel a while ago).
        //
        // Flutter clears slot.start / slot.end and re-fires PreBook
        // to refresh the slot picker. React mirrors that:
        //   1. Clear slotId / slotStart / slotEnd in form state.
        //   2. Invalidate the cached AvailableSlots query so the
        //      time-slots-section refetches with the latest data.
        //      (Section's pre-book hydration won't re-fire because
        //      hydratedRef already tracks this profile.)
        //
        // The global MutationCache onError still shows the toast
        // with the backend message; we don't suppress it.
        if (
          err instanceof ApiError &&
          err.status === 400 &&
          typeof err.body === 'string' &&
          err.body.includes('SlotPassed')
        ) {
          form.setValue('slotId', null);
          form.setValue('slotStart', null);
          form.setValue('slotEnd', null);
          // Narrowed invalidation (Samer review §5 on PR #217):
          // `useAvailableSlots` keys with practitionerId + practiceId
          // + appointmentProfileId in the params. Broad invalidation
          // (just `['available-slots']`) refetches every provider's
          // slot list on the strip — wasteful + visible flicker.
          // The form just lost its slot, so this provider/profile is
          // the only one that needs fresh data.
          const failedProviderId = form.getValues('providerId');
          const failedProfileId = form.getValues('appointmentProfileId');
          void qc.invalidateQueries({
            predicate: (query) => {
              if (query.queryKey[0] !== 'available-slots') return false;
              const params = query.queryKey[1] as
                | { practitionerId?: string; appointmentProfileId?: string }
                | undefined;
              if (!params) return false;
              return (
                params.practitionerId === failedProviderId &&
                params.appointmentProfileId === failedProfileId
              );
            },
          });
          // No toast here — the global MutationCache.onError handler
          // already surfaces the backend error message via
          // showServerError. Adding one here would double-fire.
        }
      },
    });
  };

  if (view === 'provider-selection') {
    return (
      <ProviderSelectionView
        onCancel={() => setView('create')}
        onOpenTimeDate={() => setView('time-date-selection')}
      />
    );
  }
  if (view === 'time-date-selection') {
    return <TimeDateSelectionView onCancel={() => setView('create')} />;
  }
  if (view === 'flags-selection') {
    return <FlagsPickerView onCancel={() => setView('create')} />;
  }
  if (view === 'forms-selection') {
    return <FormsPickerView onCancel={() => setView('create')} />;
  }
  if (view === 'reminder-selection') {
    return <ReminderPickerView onCancel={() => setView('create')} />;
  }
  if (view === 'reminder-editor') {
    return <ReminderEditorView onClose={() => setView('create')} />;
  }
  if (view === 'recurrence-config') {
    return <RecurrencePickerView onClose={() => setView('create')} />;
  }
  if (view === 'create-contact') {
    return <CreateContactView onClose={() => setView('create')} />;
  }
  if (view === 'edit-insurance') {
    return (
      <EditInsuranceView
        onClose={() => setView('create')}
        organizationId={organizationId}
        practiceId={practiceId}
      />
    );
  }
  if (view === 'create-patient') {
    return (
      <CreatePatientView
        onClose={() => setView('create')}
        organizationId={organizationId}
        practiceId={practiceId}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header: the title, "Open in full view" and ×. No Cancel / Create links: × closes, the footer books. */}
      <div className={cn('booking-head', modal && 'booking-head-modal')}>
        <h2 className="booking-head-title">{PANEL_TITLES[railMode]}</h2>
        <div className="booking-head-actions">
          {onToggleExpanded ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              icon={expanded ? <Minimize2 aria-hidden /> : <Maximize2 aria-hidden />}
              onClick={onToggleExpanded}
              data-booking-expand
            >
              {expanded ? 'Back to side panel' : 'Open in full view'}
            </Button>
          ) : null}
          {/* In the full view, × goes back to the rail (nothing lost); only leaving the rail's × ends the booking. */}
          <button
            type="button"
            onClick={expanded && onToggleExpanded ? onToggleExpanded : closeRail}
            aria-label={expanded ? 'Close full view' : 'Close'}
            className="booking-head-close"
          >
            <Icon name="close" size={16} aria-hidden />
          </button>
        </div>
      </div>

      {/* Scrollable body — plain div with `scrollbar-hide` so the bar
          stays off while scroll still works. */}
      <div data-booking-body className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        <div className="pb-2">
          {/* APPOINTMENT STATUS — edit mode only, for every status (Flutter
              `create_appointment_widget.dart:2537` `isEdit ?
              AppointmentStatusWidget(...) : Container()`). Hidden in reschedule
              (Flutter suppresses the status widget when isReschedule).
              PROD-1817: PA shipped `appointment-status-summary.tsx` but never
              mounted it, so the block PM shows at the top of the panel was
              simply absent here. `isEditMode` is PA's existing name for what PM
              calls `isEditOnly` — same expression. */}
          {isEditOnly || isCancelMode ? (
            <AppointmentStatusSummary
              currentStatus={editAppointment?.status}
              referenceId={editAppointment?.referenceId}
            />
          ) : null}

          {/* 1 — Location — Flutter slot 0 hides it once a provider is picked (main's order). */}
          {!providerId ? <div data-completion-target="location"><LocationSelector /></div> : null}

          {/* 2 — Date picker — same gating as location (Flutter slot 0 sub-section). */}
          {!providerId ? <div data-completion-target="date"><DatePickerSection /></div> : null}

          {/* 3 — Provider — ALWAYS locked in edit mode (Flutter `showDelete: !isEdit`), independent of status. Its
              search opens the full-screen Provider Selection view. */}
          <div data-completion-target="provider">
            <ProviderSection
              organizationId={organizationId}
              practiceId={practiceId}
              onOpenSearch={() => setView('provider-selection')}
              locked={isEditMode}
            />
          </div>

          {/* Status-gated group A — read-only when the appointment's status
              is past Booked/Proposed/Pending (Flutter `showEdit`). */}
          <SectionLock locked={sectionsLocked}>
            {/* 4 — Appointment Profile (after provider) */}
            {providerId ? (
              <div data-completion-target="visit-type">
                <AppointmentProfileSection
                  organizationId={organizationId}
                  practiceId={practiceId}
                  constrainToAppointmentTypeCode={
                    isRescheduleMode ? (editAppointment?.appointmentType?.code ?? null) : null
                  }
                />
              </div>
            ) : null}

            {/* 5 — Time slots (after profile) */}
            {providerId && appointmentProfileId ? (
              <div data-completion-target="slot">
                <TimeSlotsSection
                  practiceId={practiceId}
                  onOpenTimeDate={() => setView('time-date-selection')}
                />
              </div>
            ) : null}

            {/* 6 — Patient. Locked in EVERY edit mode (not just when the status group locks) — Flutter
                `patient_widget.dart:148` hides the change/clear controls whenever `isEdit`. */}
            <div data-completion-target="patient">
              <PatientSection
                onCreatePatient={() => setView('create-patient')}
                locked={isEditMode}
              />
            </div>

            {/* 7 — Previous Appointment (FOLLOWUP only, Flutter slot 4) */}
            <div data-completion-target="previous-appointment"><PreviousAppointmentSection /></div>

            {/* 8 — Residential Address (Home location only, Flutter slot 5) */}
            <div data-completion-target="address"><ResidentialAddressSection /></div>

            {/* 9 — Callback Contact (after patient picked, Flutter slot 6) */}
            {patientId ? (
              <CallbackContactSection onCreateContact={() => setView('create-contact')} />
            ) : null}

            {/* 10 — Reminder (auto-populated by PreBookSelectedAppointmentProfile;
                pencil opens manual-override picker) */}
            {/* Reschedule keeps the loaded reminder (PM locks it in reschedule). */}
            <SectionLock locked={isRescheduleMode}>
              <div data-completion-target="reminder"><ReminderSection onEdit={() => setView('reminder-editor')} /></div>
            </SectionLock>
          </SectionLock>

          {/* 11 — Payment (after patient). NOT gated by appointment status, but
              Flutter DOES gate it on the invoice (`payment_widget.dart:359`):
              the whole sponsor block is locked once the invoice leaves Draft
              or the appointment is cancelled. The IgnorePointer there wraps the
              entire body (incl. the Edit-insurance link), so we wrap the whole
              section here too — same as the View popup (PROD-1010). */}
          {patientId && showBillingSummary ? (
            <SectionLock locked={paymentLocked}>
              <div data-completion-target="payment">
              <PaymentSection
                organizationId={organizationId}
                practiceId={practiceId}
                onEditInsurance={() => setView('edit-insurance')}
                onAddInsurance={() => setView('edit-insurance')}
                invoiceStatus={isEditMode ? editAppointment?.invoice?.invoiceStatus ?? null : null}
              />
              </div>
            </SectionLock>
          ) : null}

          {/* Status-gated group B — Note / Flags / Send Form / Recurrence +
              the bottom action row that seeds them (Flutter slot row). */}
          <SectionLock locked={sectionsLocked}>
            <NoteSlot />
            <FlagsSection onEdit={() => setView('flags-selection')} />
            <SendFormRow onEdit={() => setView('forms-selection')} />
            <RecurrenceSlot onEdit={() => setView('recurrence-config')} />

            {/* Bottom action row — only when provider is picked */}
            {providerId && !isCancelMode ? (
              <ActionsRow
                onOpenFlags={() => setView('flags-selection')}
                onOpenSendForm={() => setView('forms-selection')}
                onOpenReminder={() => setView('reminder-selection')}
                onOpenRecurrence={() => setView('recurrence-config')}
                reminderAddDisabled={reminderAddLocked}
                hideRecurrence={isRescheduleMode}
              />
            ) : null}
          </SectionLock>

          {/* The design's status buttons in the edit rail (SchedBookingForm `.statusActions`): the existing manager's
              panel rule set (Mark As Arrived / No Show / Cancel Appointment ...), the same status mutation, arrived guard
              and cancel dialog it mounts everywhere else. */}
          {isEditOnly && editAppointment ? (
            <div className="booking-status-actions" data-slot="booking-status-actions">
              {/* "Cancel Appointment" turns the rail into its Cancel state (the design's X2) instead of opening the
                  dialog here; the Cancel state's own button opens that same dialog. */}
              <AppointmentStatusManager
                appt={editAppointment}
                variant="panel"
                onCompletePatient={onCompletePatient}
                cancelOpen={false}
                onCancelOpenChange={(next) => {
                  if (next) setRailMode('cancel');
                }}
              />
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer */}
      <PanelFooter
        mode={railMode}
        isSubmitting={isSubmitting}
        modal={modal}
        // RHF's `formState.isValid` lags behind `errors` in some flows
        // (mode: 'onChange' + zodResolver doesn't always flip isValid
        // back to true after the last error clears). Deriving validity
        // from the errors object directly is reliable.
        isValid={Object.keys(form.formState.errors).length === 0}
        missingFields={collectErrorPaths(form.formState.errors)}
        rawErrors={form.formState.errors}
        onSave={handleSave}
        onCancel={closeRail}
        onReschedule={isEditOnly ? () => setRailMode('reschedule') : undefined}
        onCancelAppointment={isCancelMode ? () => setCancelDialogOpen(true) : undefined}
      />

      {/* Cancel state: the same cancel dialog the status manager mounts (reason, recurring scope, the same mutations). */}
      {isCancelMode && editAppointment ? (
        <CancelAppointmentDialog
          open={cancelDialogOpen}
          onOpenChange={(next) => {
            setCancelDialogOpen(next);
            if (!next) void qc.invalidateQueries({ queryKey: ['appointment-details'] });
          }}
          target={{
            appointmentId: editAppointment.id,
            patientId: findPatientParticipant(editAppointment.participant)?.patient?.id,
            practitionerId: editAppointment.participant?.find((p) => {
              const t = (p as { referenceType?: string }).referenceType?.toLowerCase();
              return t === 'practitioner' || t === 'provider';
            })?.provider?.id,
            startISO: editAppointment.start ?? undefined,
            recurringId: editAppointment.recurring?.id ?? undefined,
            patientFirstName: findPatientParticipant(editAppointment.participant)?.patient?.firstName,
          }}
        />
      ) : null}
    </div>
  );
}

export function CreateAppointmentPanel({
  open,
  onClose,
  initialValues,
  hideFullView = false,
  ...props
}: CreateAppointmentPanelProps) {
  // "Open in full view" (Antonin, 15 Oct): the same form in a centred dialog, every value kept. ONE form instance: it
  // renders into a stable host node, and the host moves between the rail and the dialog. React never remounts it (the
  // portal's container never changes), so values, the open sub-view, searches and fetched slots survive the trip both ways.
  const [expanded, setExpanded] = useState(false);
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const [host] = useState(() => {
    const el = document.createElement('div');
    el.className = 'booking-host';
    return el;
  });
  const railSlotRef = useRef<HTMLDivElement | null>(null);
  // A moved node forgets its scroll: read it while it is still on screen, give it back once it lands.
  const scrollTopRef = useRef(0);
  const body = useCallback(() => host.querySelector<HTMLElement>('[data-booking-body]'), [host]);
  const moveHost = useCallback(
    (to: HTMLElement) => {
      if (host.parentNode === to) return;
      to.appendChild(host);
      const el = body();
      if (el) el.scrollTop = scrollTopRef.current;
    },
    [host, body],
  );
  const setExpandedKeepingScroll = useCallback(
    (next: boolean) => {
      scrollTopRef.current = body()?.scrollTop ?? 0;
      setExpanded(next);
    },
    [body],
  );
  // Callback refs place the host during the commit, before the form's own layout effects run.
  const railSlot = useCallback(
    (el: HTMLDivElement | null) => {
      railSlotRef.current = el;
      if (el && !expandedRef.current) moveHost(el);
    },
    [moveHost],
  );
  const modalSlot = useCallback(
    (el: HTMLDivElement | null) => {
      if (el) moveHost(el);
    },
    [moveHost],
  );
  useLayoutEffect(() => {
    const rail = railSlotRef.current;
    if (!expanded && rail) moveHost(rail);
  }, [expanded, moveHost]);
  // Ending the booking (a save that closes, ×) leaves nothing expanded for the next one.
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  if (!open) return null;

  const formKey = [
    initialValues?.providerId ?? '',
    initialValues?.appointmentProfileId ?? '',
    initialValues?.slotStart ?? '',
    initialValues?.selectedDate ?? '',
    initialValues?.referenceAppointmentId ?? '',
  ].join('|');

  return (
    <>
      {/* The rail: on the RIGHT of the calendar, fixed width, side border. While the form is in the full view the rail
          steps aside (the calendar takes the room back) and comes back on return. */}
      <div data-edge="right" data-booking-rail data-expanded={expanded} className="booking-rail">
        <div ref={railSlot} className="booking-host" />
      </div>

      {createPortal(
        <AppointmentFormProvider key={formKey} initialValues={initialValues}>
          <CreateAppointmentForm
            onClose={onClose}
            modal={expanded}
            expanded={expanded}
            onToggleExpanded={hideFullView ? undefined : () => setExpandedKeepingScroll(!expanded)}
            {...props}
          />
        </AppointmentFormProvider>,
        host,
      )}

      {/* The full view: the DS Modal at the large width (--dialog-width-large, 800), 90 vh. ×, Esc and the backdrop all
          go back to the rail; only the rail's × (or a saved booking) ends the booking. */}
      <Modal
        open={expanded}
        onClose={() => setExpandedKeepingScroll(false)}
        title={PANEL_TITLES[props.mode ?? 'create']}
        titleHidden
        variant="bleed"
        scrollBody
        className="booking-full-view"
      >
        <div ref={modalSlot} className="booking-host" />
      </Modal>
    </>
  );
}
